import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../../../components/app/DeleteModal";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useArchiveState } from "../../../../../../../hooks/useArchiveState";
import { useArchiveEmail } from "../../../../../../../queries/patient/use-email-archive";
import { useGetPatientPortalEmails } from "../../../../../../../queries/patient/use-get-all-email";
import { formatDateTime } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../../../utils/utils";
import ViewEmail from "./ViewEmail";

export type EmailCommunicationFilters = {
  patientId?: string;
  startDate?: string;
  endDate?: string;
  searchString?: string;
  archive?: boolean;
};

const PatientEmailTable = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: string };
  const { mutateAsync: archiveMutation } = useArchiveEmail();
  const viewEmailDisclosureHost = useDisclosure();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);
  const [archiveEmail, setArchiveEmail] = useState<boolean>(false);

  const { emailFilter } = useOutletContext<any>();
  const {
    data: emailList,
    refetch,
    isLoading: tableDataLoading,
    isRefetching: tableRefetching,
  } = useGetPatientPortalEmails({
    // templateTypes: GetAllEmailHistoryTemplateTypesEnum.Email,
    searchString: emailFilter?.searchString,
    startDate: emailFilter?.startDate,
    endDate: emailFilter?.endDate,
    gridSorting,
    pagination,
    patientId,
    archive: emailFilter?.archive?.value || false,
    xTENANTID: getXtenantId(),
  });

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "sentBy",
      flex: 0.3,
      minWidth: 100,
      disableColumnMenu: true,
      headerName: "Sent By",
      renderCell: ({ value }) => <RenderWithTooltip value={value} />,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 0.3,
      minWidth: 100,
      renderCell: ({ value }) => <RenderWithTooltip value={value} />,
    },
    {
      field: "sendDate",
      flex: 0.5,
      minWidth: 150,
      headerName: "Sent Date & Time",
      renderCell: ({ row }) => (
        <RenderWithTooltip
          value={row?.sendDate ? formatDateTime(row.sendDate) : "-"}
        />
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      minWidth: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => (
        <ActionMenu
          options={[
            { value: "view", label: "View" },
            { value: "archive", label: "Archive" },
          ]}
          onActionClick={(v) => {
            if (v === "view") {
              viewEmailDisclosureHost.open(row);
            }
            if (v === "archive") {
              setArchiveEmail(row?.archive);
              deleteDisc.open(row?.uuid);
            }
          }}
        />
      ),
    },
  ]);

  return (
    <Flex flexDirection={"column"} height={"100%"}>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archiveEmail, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to Archive this email`}
      />
      <ViewEmail title="View Email" disclosureHost={viewEmailDisclosureHost} />
      <Box height={"100%"}>
        <CustomDataGridV2
          rows={emailList?.data?.content ?? []}
          getRowId={(row) => row?.uuid}
          rowCount={emailList?.data.page.totalElements}
          loading={tableDataLoading || tableRefetching}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
        />
      </Box>
    </Flex>
  );
};

export default PatientEmailTable;
